<template>
  <div v-if="grupo">
    <div class="card-usuario">
      <div class="reserva-media">
        <div class="media-header">
          <div class="media-background position-relative pointer" @click="goToDetalle">
            <img
              v-if="grupo.viaje.imagenExperiencia"
              :src="grupo.viaje.imagenExperiencia"
              :class="{ 'image--past': isExperienciaPasada }" />
            <img v-else src="@/assets/img/fondo_mygocu.png" :class="{ 'image--past': isExperienciaPasada }" />
            <span v-if="isExperienciaPasada" class="past-experience-badge semibold small text-nowrap">
              {{ t('experiencia.pasada') }}
            </span>
            <div v-if="showQrBonosButton" class="qr-button pointer" @click.stop="showModalQr = true">
              <CustomIcon :icon="qrIcon" size="42px" class="text-primary" size-mobile="42px" />
            </div>
          </div>
        </div>
        <div class="media-overlap-body">
          <div class="reserva-content">
            <div class="reserva-details pt-15 px-20 pb-20">
              <div class="reserva-title-wrapper overflow-hidden">
                <MarqueeText class="text-primary h6 semibold mb-15">{{ grupo.viaje.nombre }}</MarqueeText>
              </div>
              <div class="reserva-location-row d-flex align-items-center mb-10">
                <CustomIcon :icon="ubicacionIcon" size="16px" class="text-primary me-2" />
                <span class="p text-primary">{{ grupo.viaje.lugarExperiencia }}</span>
              </div>

              <div class="reserva-date-row d-flex align-items-center mb-20">
                <CustomIcon :icon="calendarioIcon" size="16px" class="text-primary me-2" />
                <span class="p text-primary">{{ experienceDateRange }}</span>
              </div>
              <div class="details-panel overflow-hidden" :class="{ 'rounded-bottom': isInscripcion && isCollapsed }">
                <div class="details-panel-header px-15 py-2">
                  <div
                    class="details-toggle-row text-primary d-flex align-items-center justify-content-between w-100"
                    :class="{ pointer: occupants?.length > 0 }"
                    @click="occupants?.length > 0 && toggleCollapse()">
                    <div class="medium text-primary p">
                      <span>
                        {{ $t('general.asistentes') }}
                        <template v-if="hasAlojamiento && !grupo.isB2b">
                          ({{ occupants.length }}/{{ grupo.habitacion.numPlazas }})
                        </template>
                        <template v-else-if="grupo.isB2b">
                          ({{ occupants.length }}/{{ props.grupo.viaje.invitacionGrupo.plazas }})
                        </template>
                        <template v-else> ({{ occupants.length }}) </template>
                      </span>
                    </div>

                    <CustomIcon
                      v-if="occupants?.length > 0"
                      :icon="isCollapsed ? desplegarIcon : plegarIcon"
                      size="14px"
                      class="pointer text-primary" />
                  </div>
                </div>
                <div v-if="!isInscripcion || !isCollapsed" class="details-panel-body px-15">
                  <transition name="collapse">
                    <div v-show="!isCollapsed" class="occupants-list pt-10">
                      <CardOccupancyMini
                        :max-size="!grupo.isB2b && grupo.habitacion ? grupo.habitacion.numPlazas : -1"
                        :occupants-info="occupants" />
                    </div>
                  </transition>
                  <div v-if="!isInscripcion" class="reserva-payments">
                    <div class="payment-line text-primary d-flex align-items-center justify-content-between py-10">
                      <span class="small bold text-uppercase">{{ $t('subtitulo.importeTotal') }}</span>
                      <span class="small bold text-nowrap"
                        >{{ formatPrice(totalPrice) }} {{ agenciaStore.getCurrency }}</span
                      >
                    </div>
                    <div class="payment-line text-primary d-flex align-items-center justify-content-between py-10">
                      <span class="small bold">{{ $t('reserva.pendientePago') }}</span>
                      <span class="small bold text-nowrap"
                        >{{ formatPrice(pricePendingPayment) }} {{ agenciaStore.getCurrency }}</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="reserva-actions mx-3 pb-4 text-center">
              <div class="reserva-actions-row row m-0 gap-20">
                <div class="col m-0 p-0 action-col">
                  <MyGocuButton secondary :label="t('botones.verDetalles')" @click="goToDetalle" />
                </div>
                <div v-if="showPagarButton" class="col m-0 p-0 action-col">
                  <MyGocuButton primary :label="t('botones.pagar')" @click="showModalPagar = true" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ModalPagarReserva :grupo="grupo" :modal-active="showModalPagar" @close-modal="showModalPagar = false" />

    <ModalQrReserva :grupo="grupo" :modal-active="showModalQr" @close-modal="showModalQr = false" />
  </div>
</template>

<script setup>
  import MyGocuButton from '@/components/common/buttons/MyGocuButton.vue';
  import { ref, computed } from 'vue';
  import CardOccupancyMini from '@/modules/viajesGrupos/components/CardOccupancyMini.vue';
  import { useRouter } from 'vue-router';
  import ModalPagarReserva from 'src/components/modales/Reserva/ModalPagarReserva';
  import { useI18n } from 'vue-i18n';
  import { formatDateDDmmm, formatDateIntervalDDmmm, isBeforeToday } from '@/helpers/dateUtiles';
  import { UsuarioProximasReservasDetalle } from '@/router/RouterNames';
  import { ESTADOS_RESERVA } from '@/constants/reservaConstants';
  import ModalQrReserva from 'src/components/modales/Reserva/ModalQrReserva';
  import {
    calculateTotalPendiente,
    occupantsManagedReservations,
    occupantsNotManagedReservations,
  } from '@/services/utils/GrupoUtils';
  import currency from 'currency.js';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import { ESTADOS_INVITACION_GRUPO, TIPOS_PAGO } from '@/constants/viajeConstants';
  import { formatPrice } from '@/helpers/numberUtils';
  import desplegarIcon from 'src/assets/icons/raw/desplegar.svg';
  import plegarIcon from 'src/assets/icons/raw/plegar.svg';
  import ubicacionIcon from '@/assets/icons/raw/ubicacion.svg';
  import calendarioIcon from '@/assets/icons/raw/calendario.svg';
  import qrIcon from '@/assets/icons/qr.svg';
  import CustomIcon from '@/components/CustomIcon.vue';
  import MarqueeText from '@/components/common/MarqueeText.vue';

  const props = defineProps({
    grupo: {
      type: Object,
      required: true,
    },
  });

  const { t } = useI18n();
  const router = useRouter();
  const agenciaStore = agenciaStoreModule();
  const isCollapsed = ref(true);

  const showModalPagar = ref(false);
  const showModalQr = ref(false);

  const pricePendingPayment = computed(() =>
    props.grupo.isB2b
      ? currency(props.grupo.viaje.invitacionGrupo.importeTotalEstimado)
      : calculateTotalPendiente(props.grupo.reservas)
  );

  const isInscripcion = computed(
    () =>
      props.grupo.viaje.tipoPago === TIPOS_PAGO.SIN_PAGO ||
      props.grupo.viaje.invitacionGrupo?.estado === ESTADOS_INVITACION_GRUPO.ACEPTADA
  );
  const showPagarButton = computed(
    () =>
      !props.grupo.isB2b &&
      props.grupo.reservas.some(({ estado }) => estado !== ESTADOS_RESERVA.CANCELADA) &&
      pricePendingPayment.value > 0 &&
      !isInscripcion.value
  );

  const showQrBonosButton = computed(() => {
    if (!props.grupo.reservas || props.grupo.reservas.length === 0) {
      return false;
    }
    const isNotCancelled = props.grupo.reservas.some(({ estado }) => estado !== ESTADOS_RESERVA.CANCELADA);
    return isNotCancelled && pricePendingPayment.value <= 0;
  });

  const experienceDateRange = computed(() => {
    const { fechaInicio, fechaFin } = props.grupo.viaje;
    const range =
      fechaInicio === fechaFin ? formatDateDDmmm(fechaInicio) : formatDateIntervalDDmmm(fechaInicio, fechaFin);
    return range.toLowerCase();
  });

  const isExperienciaPasada = computed(() => isBeforeToday(props.grupo.viaje.fechaFin));

  const totalPrice = computed(() => {
    return props.grupo.isB2b
      ? currency(props.grupo.viaje.invitacionGrupo.importeTotalEstimado)
      : props.grupo.reservas.reduce((acc, { precioTotal }) => acc.add(precioTotal), currency(0)).value;
  });

  const hasAlojamiento = computed(() => props.grupo.habitacion);

  const occupants = computed(() => {
    const ocupantesGestionados = occupantsManagedReservations(props.grupo) || [];
    const ocupantesNoGestionados = occupantsNotManagedReservations(props.grupo) || [];
    return [...ocupantesNoGestionados, ...ocupantesGestionados];
  });

  function toggleCollapse() {
    isCollapsed.value = !isCollapsed.value;
  }

  function goToDetalle() {
    router.push({
      name: UsuarioProximasReservasDetalle,
      params: { grupoId: props.grupo.id },
      query: { isB2b: props.grupo.isB2b },
    });
  }
</script>

<style lang="scss" scoped>
  .card-usuario {
    min-width: 270px;
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
    border-radius: var(--bs-border-radius-xl);
    overflow: hidden;
    background-color: white;
  }

  .reserva-media {
    width: 100%;
  }

  .reserva-details {
    background-color: white;
  }

  .media-header {
    position: relative;
    height: 120px;
    background-color: white;
  }

  .media-background {
    height: 120px;
    overflow: hidden;
  }

  .media-background img {
    height: 120px;
    width: 100%;
    object-fit: cover;
  }

  .media-background img.image--past {
    filter: grayscale(100%);
  }

  .past-experience-badge {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    background-color: rgba(var(--bs-primary-rgb), 0.7);
    border-radius: var(--bs-border-radius-xl);
    padding: 6px 12px;
    z-index: 1;
  }

  .qr-button {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    border-radius: var(--bs-border-radius-xl);
    box-shadow: 4px 6px 20px 0 rgba(var(--bs-primary-rgb), 0.1), 2px 4px 5px 0 rgba(var(--bs-primary-rgb), 0.1);
  }

  .details-panel {
    border-radius: var(--bs-border-radius-xl) !important;
    border: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .details-panel-header {
    background-color: rgba(var(--bs-primary-rgb), 0.1);
  }

  .details-panel-body {
    background-color: white;
  }

  .occupants-list :deep(.row-occupancy) {
    flex-direction: column;
    flex-wrap: nowrap;
  }

  .occupants-list :deep(.user-container) {
    width: 100%;
  }

  .payment-line {
    gap: 8px;
  }

  .payment-line + .payment-line {
    border-top: 1px solid rgba(var(--bs-primary-rgb), 0.2);
  }

  .action-col :deep(.mg-btn-root) {
    width: 100%;
  }

  .action-col :deep(.mg-btn) {
    width: 100%;
  }

  @media (min-width: 768px) {
    .card-usuario {
      max-width: 290px;
    }

    .action-col :deep(.mg-btn) {
      font-size: 12px;
    }
  }
</style>
